import { useEffect, useRef, useState } from "react";
import { Table, Tooltip } from "antd";
import { DownloadOutlined, ReloadOutlined, InfoCircleOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { downloadExcludedRecordsWorkbook, downloadWorkbook, previewError } from "../api/reports";
import { useWorkbookGrid, useWorkbookGridPage } from "../state/useWorkbookGrid";
import type { useReportPreview } from "../state/useReportPreview";
import type { PreviewArtifact, PreviewGrid, PreviewGridSection } from "../types";
import { formatEffectiveDate } from "./ReportCatalog";
import { ExcludedRecordSummary } from "../../ReportConfiguration/components/shared/ExcludedRecordSummary";

interface Props {
    preview: ReturnType<typeof useReportPreview>;
}
export function WorkbookPreview({ preview }: Props) {
    const { details, artifact, loading, error, refresh, cancel } = preview;
    return <section className="rcp-viewer" aria-label="Report preview">
        <div className="rcp-viewer-header"><div>
            <div className="rcp-eyebrow">{details ? `${details.report.planName} / ${details.report.folderName}` : "Report output"}</div>
            <h2>{details?.report.reportName || "Loading report..."}</h2>
        </div><div className="rcp-actions">
            <button disabled={loading || !details} onClick={refresh} title="Generate a fresh report using its saved settings"><ReloadOutlined aria-hidden="true" /> Refresh report</button>
            {artifact && <ExcludedRecordsDownloadButton key={`excluded-${artifact.id}`} artifact={artifact} />}
            {artifact && <DownloadButton key={artifact.id} artifact={artifact} />}
        </div></div>
        {details && <div className="rcp-context">
            {artifact && <><div><small>Reporting period</small><strong>{artifact.reportingPeriod || "Saved report settings"}</strong></div>
                <div><small>Generated</small><span>{new Date(artifact.generatedUtc).toLocaleString()}</span></div>
                <div><small>Effective from</small><span>{formatEffectiveDate(artifact.effectiveFrom)}</span></div></>}
        </div>}
        {loading && <div className="rcp-state" role="status"><span className="rcp-spinner" /><h2>{details ? "Generating your report..." : "Loading report..."}</h2>
            <p>Large reports may take a few minutes.</p><button onClick={cancel}>Cancel</button></div>}
        {error && <div className="rcp-state" role="alert"><p>{error}</p><button onClick={refresh}>Retry</button></div>}
        {artifact && <>
            {!artifact.hasData && artifact.skippedSections.length === 0 && <p className="rcp-warning" role="status">No data matched the saved report filters. The report layout is shown below.</p>}
            {!!artifact.skippedSections.length && <details className="rcp-warning"><summary>Partial report - {artifact.skippedSections.length} section(s) unavailable. View details</summary>
                <ul>{artifact.skippedSections.map((note, i) => <li key={i}>{note}</li>)}</ul>
                <p>Your download includes the same available sections.</p></details>}
            {!!artifact.warnings.length && <details className="rcp-warning"><summary>Report notes ({artifact.warnings.length})</summary>
                <ul>{artifact.warnings.map((note, i) => <li key={i}>{note}</li>)}</ul></details>}
            <ReportOutputViews key={artifact.id} artifact={artifact} />
        </>}
    </section>;
}

function ReportOutputViews({ artifact }: { artifact: PreviewArtifact }) {
    const [view, setView] = useState<"output" | "exclusions">("output");
    return <>
        <div className="rcp-output-tabs" role="tablist" aria-label="Report views" onKeyDown={event => {
            let nextView: "output" | "exclusions";
            if (event.key === "Home") nextView = "output";
            else if (event.key === "End") nextView = "exclusions";
            else if (event.key === "ArrowLeft" || event.key === "ArrowRight") nextView = view === "output" ? "exclusions" : "output";
            else return;
            event.preventDefault();
            setView(nextView);
            event.currentTarget.querySelector<HTMLButtonElement>(`[data-view="${nextView}"]`)?.focus();
        }}>
            <button type="button" role="tab" data-view="output" id={`report-output-tab-${artifact.id}`} aria-selected={view === "output"} tabIndex={view === "output" ? 0 : -1} aria-controls={`report-output-${artifact.id}`} onClick={() => setView("output")}>Report Output</button>
            <button type="button" role="tab" data-view="exclusions" id={`report-exclusions-tab-${artifact.id}`} aria-selected={view === "exclusions"} tabIndex={view === "exclusions" ? 0 : -1} aria-controls={`report-exclusions-${artifact.id}`} onClick={() => setView("exclusions")}>Excluded Record Summary</button>
        </div>
        <div role="tabpanel" tabIndex={0} aria-labelledby={`report-output-tab-${artifact.id}`} id={`report-output-${artifact.id}`} hidden={view !== "output"}><SheetViewer artifact={artifact} /></div>
        <div role="tabpanel" tabIndex={0} aria-labelledby={`report-exclusions-tab-${artifact.id}`} className="rcp-exclusion-panel" id={`report-exclusions-${artifact.id}`} hidden={view !== "exclusions"}>
            <ExcludedRecordSummary source={{ reportId: artifact.reportId, configVersionId: artifact.configVersionId, artifactId: artifact.id }} active={view === "exclusions"} />
        </div>
    </>;
}

function DownloadButton({ artifact }: { artifact: PreviewArtifact }) {
    const controller = useRef<AbortController | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    useEffect(() => () => controller.current?.abort(), []);
    const download = async () => {
        controller.current?.abort();
        const request = new AbortController(); controller.current = request;
        setLoading(true); setError("");
        try {
            const blob = await downloadWorkbook(artifact.id, request.signal);
            if (request.signal.aborted) return;
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a"); link.href = url; link.download = artifact.fileName;
            document.body.appendChild(link); link.click(); link.remove();
            window.setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e) {
            const message = await previewError(e);
            if (!request.signal.aborted) setError(message);
        } finally { if (!request.signal.aborted) setLoading(false); }
    };
    return <div><button className="rcp-primary" disabled={loading} onClick={download} title="Download this report as an Excel file"><DownloadOutlined aria-hidden="true" /> {loading ? "Downloading..." : "Download Report"}</button>
        {error && <small role="alert">{error}</small>}</div>;
}

function ExcludedRecordsDownloadButton({ artifact }: { artifact: PreviewArtifact }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const download = async () => {
        if (loading) return;
        setLoading(true); setError("");
        try {
            const response = await downloadExcludedRecordsWorkbook(artifact.reportId, artifact.configVersionId);
            const blob = response.data as Blob;
            const contentDisposition = response.headers?.["content-disposition"] || "";
            const fileNameMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
            const fileName = fileNameMatch?.[1] || "Excluded Records.xlsx";
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a"); link.href = url; link.download = fileName;
            document.body.appendChild(link); link.click(); link.remove();
            window.setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e) {
            setError(await previewError(e));
        } finally { setLoading(false); }
    };
    return <div><button disabled={loading} onClick={download} title="Download the records each metric left out (same reporting period and health plan), with the reason for each."><DownloadOutlined aria-hidden="true" /> {loading ? "Generating..." : "Download Excluded Records"}</button>
        {error && <small role="alert">{error}</small>}</div>;
}

function SheetViewer({ artifact }: { artifact: PreviewArtifact }) {
    const [index, setIndex] = useState(artifact.sheets[0]?.index ?? 0);
    const selectedSheet = artifact.sheets.find(s => s.index === index);
    const name = selectedSheet?.name || "Report";
    return <>
        <div className="rcp-sheet-controls">
            <div className="rcp-sheetbar" role="group" aria-label="Workbook sheets">
                {artifact.sheets.map(s => <button key={s.index} aria-pressed={s.index === index} onClick={() => setIndex(s.index)}>{s.name}</button>)}
            </div>
        </div>
        <div className="rcp-output-shell">
            <GridDocument artifact={artifact} sheetIndex={index} name={name} />
        </div>
    </>;
}

function GridDocument({ artifact, sheetIndex, name }: { artifact: PreviewArtifact; sheetIndex: number; name: string }) {
    const grid = useWorkbookGrid(artifact.id, sheetIndex);
    return <>
        <div className="rcp-document rcp-document--grid" aria-busy={grid.loading}>
            {grid.loading ? <div className="rcp-state" role="status"><span className="rcp-spinner" /><h2>Preparing {name} data...</h2><p>Preparing your report data.</p><button onClick={grid.cancel}>Cancel</button></div>
                : grid.error ? <div className="rcp-state" role="alert"><p>{grid.error}</p><button onClick={grid.retry}>Retry data preview</button></div>
                : grid.grid && <GridTable key={`${artifact.id}/${sheetIndex}`} grid={grid.grid} artifactId={artifact.id} sheetIndex={sheetIndex} />}
        </div>
        <div className="rcp-document-tools">
            <span className="rcp-muted"><InfoCircleOutlined aria-hidden="true" /> Explore report data by metric. Download Report for complete workbook formatting and charts.</span>
        </div>
    </>;
}

function GridTable({ grid, artifactId, sheetIndex }: { grid: PreviewGrid; artifactId: string; sheetIndex: number }) {
    if (!grid.sections.length) return <div className="rcp-state"><h2>No data to display</h2><p>Download Report to view the complete workbook.</p></div>;
    return <div className="rcp-grid-preview">
        <div className="rcp-grid-title">
            <h3>{grid.sheetName}</h3>
            <span>{grid.totalRows.toLocaleString()} row{grid.totalRows === 1 ? "" : "s"}</span>
        </div>
        {grid.isTruncated && <p className="rcp-warning" role="status">Some metric sections are shortened in the browser. Download Report for the complete data.</p>}
        <div className="rcp-grid-sections">
            {grid.sections.map((section, index) => <MetricGridSection key={`${section.title}-${index}`} section={section} artifactId={artifactId} sheetIndex={sheetIndex} sectionIndex={index} />)}
        </div>
    </div>;
}

function MetricGridSection({ section: initial, artifactId, sheetIndex, sectionIndex }: { section: PreviewGridSection; artifactId: string; sheetIndex: number; sectionIndex: number }) {
    const remote = useWorkbookGridPage(artifactId, sheetIndex, sectionIndex, initial);
    const section = remote.section;
    const isTotalColumn = (column: string) => column.trim().toLowerCase() === "total";
    const isNumericColumn = (index: number) => section.rows.every(row => {
        const value = row[index];
        return !value || /^-?\(?\$?[\d,]+(\.\d+)?%?\)?$/.test(value.trim());
    });
    const columnWidth = (column: string, index: number) => {
        const longest = Math.max(column.length, ...section.rows.slice(0, 100).map(row => (row[index] || "").length));
        if (index === 0) return Math.min(260, Math.max(150, longest * 7 + 28));
        return Math.min(isNumericColumn(index) ? 160 : 220, Math.max(76, longest * 7 + 28));
    };
    const columns: ColumnsType<Record<string, string>> = section.columns.map((column, index) => ({
        title: column || `Column ${index + 1}`,
        dataIndex: `c${index}`,
        key: `c${index}`,
        ellipsis: { showTitle: false },
        render: (value: string) => <Tooltip title={value} mouseEnterDelay={0.5}><span>{value}</span></Tooltip>,
        width: columnWidth(column, index),
        align: index > 0 && isNumericColumn(index) ? "right" : "left",
        className: isTotalColumn(column) ? "rcp-grid-total-column" : undefined,
    }));
    const data = section.rows.map((row, rowIndex) => {
        const item: Record<string, string> = { key: String(rowIndex) };
        section.columns.forEach((_, columnIndex) => item[`c${columnIndex}`] = row[columnIndex] || "");
        return item;
    });
    return <section className="rcp-grid-section">
        <div className="rcp-grid-section-header">
            <div>
                <h4>{section.title}</h4>
                <small>{section.kind === "detail" ? "Detailed records" : "Summary"}</small>
            </div>
            <span>{section.totalRows.toLocaleString()} row{section.totalRows === 1 ? "" : "s"}</span>
        </div>
        {!!section.warnings.length && <details className="rcp-warning"><summary>Metric notes ({section.warnings.length})</summary>
            <ul>{section.warnings.map((note, i) => <li key={i}>{note}</li>)}</ul></details>}
        {section.isTruncated && <p className="rcp-warning" role="status">Showing the first {section.rows.length.toLocaleString()} rows for this metric.</p>}
        {remote.error && <div role="alert" className="rcp-error">{remote.error} <button onClick={remote.retry}>Retry page</button></div>}
        <Table
            loading={remote.loading}
            columns={columns}
            dataSource={remote.error ? [] : data}
            pagination={initial.isPaged ? { current: remote.page, pageSize: remote.pageSize, total: initial.totalRows,
                showSizeChanger: true, showQuickJumper: true, pageSizeOptions: [25, 50, 100], onChange: remote.change,
                showTotal: (total, range) => `${range[0]} - ${range[1]} of ${total.toLocaleString()} rows` } : data.length > 25 ? { pageSize: 25, showSizeChanger: false, showTotal: (total, range) => `${range[0]} - ${range[1]} of ${total} rows` } : false}
            rowKey="key"
            size="small"
            rowClassName={(row) => String(row.c0 || "").trim().toLowerCase() === "total" ? "rcp-grid-total-row" : ""}
            scroll={{ x: Math.max(columns.reduce((sum, column) => sum + Number(column.width || 100), 0), 520) }}
        />
    </section>;
}
